import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Map, Route } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { MultiSelect } from 'components/ui/multi-select';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import { useCountries, useSubdivisions } from 'hooks/useReferentials';
import type { Subdivision } from 'api/referentialsApi';
import { subdivisionLabel } from 'utils/subdivisionLabel';
import { cn } from 'lib/utils';
import { controlId, errorId, validationMessage, type FormErrors } from './validation';
import ZoneChip from './ZoneChip';

interface ScopeSectionProps {
  countryCode: string;
  geoAreaIds: number[];
  /** F18 — a scored fallback zone, weighed like the edge of the distance cap, never a substitute. */
  secondaryGeoAreaIds: number[];
  onChange: (patch: { countryCode?: string; geoAreaIds?: number[]; secondaryGeoAreaIds?: number[] }) => void;
  readOnly: boolean;
  /** The scope is blocking on a new record and merely indicative on an old one that predates it. */
  creating?: boolean;
  errors?: FormErrors;
}

/**
 * A picker offers active values only; a deactivated one the zone already carries stays so
 * re-saving does not drop it. A department the other zone holds is greyed (R5).
 */
const zoneOptions = (all: Subdivision[], countryCode: string, own: number[], other: number[], taken: string) =>
  all
    .filter((s) => s.country_code === countryCode && (s.is_active || own.includes(s.id)))
    .map((s) => ({
      value: String(s.id),
      label: `${s.code} — ${s.name}`,
      disabled: other.includes(s.id),
      hint: other.includes(s.id) ? taken : undefined,
    }));

/** The rows, in pick order; an id whose referential row has not arrived (or was purged) is skipped. */
const resolveZones = (all: Subdivision[], ids: number[]): Subdivision[] =>
  ids.map((id) => all.find((s) => s.id === id)).filter((zone): zone is Subdivision => zone !== undefined);

/**
 * The geographic scope, on `user_geo_areas` and never on `users.sectors` — those are Globe Data
 * *commercial* sectors consumed by `/issues/bySectors`, and writing a department code in there
 * breaks two other features.
 */
const ScopeSection = ({
  countryCode,
  geoAreaIds,
  secondaryGeoAreaIds,
  onChange,
  readOnly,
  creating = false,
  errors = {},
}: ScopeSectionProps) => {
  const { t } = useTranslation();
  const { data: countries } = useCountries();
  const { data: subdivisions } = useSubdivisions();
  const [pendingCountry, setPendingCountry] = useState<string | null>(null);

  const country = (countries ?? []).find((entry) => entry.code === countryCode) ?? null;

  const all = subdivisions ?? [];
  const options = zoneOptions(all, countryCode, geoAreaIds, secondaryGeoAreaIds, t('Already in the secondary zone'));
  const secondaryOptions = zoneOptions(all, countryCode, secondaryGeoAreaIds, geoAreaIds, t('Already in the main zone'));
  const selectedZones = resolveZones(all, geoAreaIds);
  const secondarySelectedZones = resolveZones(all, secondaryGeoAreaIds);

  const zoneLabel = country ? subdivisionLabel(t, country.subdivision_type, 2) : t('Areas covered');

  // Changing country empties both zones, so it asks first; cancelling leaves both as they were.
  const confirmCountry = () => {
    if (pendingCountry === null) return;
    onChange({ countryCode: pendingCountry, geoAreaIds: [], secondaryGeoAreaIds: [] });
    setPendingCountry(null);
  };

  /**
   * Radix mirrors the select into a hidden native one as soon as the trigger sits inside a
   * `<form>`, and that mirror fires one change with an empty value while the options are still
   * unmounted. An empty code is never a choice a person made — the picker offers none.
   */
  const pickCountry = (code: string) => {
    if (!code || code === countryCode) return;
    if (geoAreaIds.length === 0 && secondaryGeoAreaIds.length === 0) {
      onChange({ countryCode: code, geoAreaIds: [], secondaryGeoAreaIds: [] });
      return;
    }
    setPendingCountry(code);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Map className="h-4 w-4" aria-hidden="true" />
            {t('Geographic scope')}
          </CardTitle>
          {/* The design's grey chain pill. Built from the two labels rather than a key of its
              own: the second half is the country's own word and already translated. */}
          <span className="inline-flex h-[26px] shrink-0 items-center whitespace-nowrap rounded-full bg-zinc-100 px-2.5 text-[11.5px] font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
            {t('Country')} → {zoneLabel}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="min-w-0 space-y-1.5">
            <Label htmlFor={controlId('countryCode')}>
              {t('Country')}
              {creating && <span className="text-rose-500"> *</span>}
            </Label>
            {/* Always a string, never undefined: switching would make Radix warn about a select
                going from uncontrolled to controlled once the record loads. */}
            <Select value={countryCode} onValueChange={pickCountry} disabled={readOnly}>
              <SelectTrigger
                id={controlId('countryCode')}
                aria-invalid={errors.countryCode ? true : undefined}
                aria-describedby={errors.countryCode ? errorId('countryCode') : undefined}
                className={cn(errors.countryCode && 'border-rose-400 dark:border-rose-500')}
              >
                <SelectValue placeholder={t('Select...')} />
              </SelectTrigger>
              <SelectContent>
                {(countries ?? [])
                  .filter((entry) => entry.is_active || entry.code === countryCode)
                  .map((entry) => (
                    <SelectItem key={entry.code} value={entry.code}>
                      {entry.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {errors.countryCode && (
              <p id={errorId('countryCode')} className="text-xs text-rose-600 dark:text-rose-400">
                {validationMessage(t, errors.countryCode)}
              </p>
            )}
          </div>

          {/* No picker at all in read mode: the chips below already say the whole truth, and a
              disabled dropdown only invites a click that goes nowhere. */}
          {!readOnly && (
            <div className="min-w-0 space-y-1.5">
              <Label>
                {zoneLabel}
                {creating && <span className="text-rose-500"> *</span>}
              </Label>
              <MultiSelect
                id={controlId('geoAreaIds')}
                aria-label={zoneLabel}
                aria-describedby={errors.geoAreaIds ? errorId('geoAreaIds') : undefined}
                options={options}
                selected={geoAreaIds.map(String)}
                onChange={(values) => onChange({ geoAreaIds: values.map(Number) })}
                disabled={!countryCode}
                searchable
                joinedLabels
                placeholder={
                  countryCode && options.length === 0 ? t('No area for this country yet') : t('Select...')
                }
              />
            </div>
          )}
        </div>

        {/* Compact chips that wrap (planche 2d): the card's country is already said above. */}
        {selectedZones.length === 0 ? (
          /* Same box, two voices: the refusal on a creation, the standing hint everywhere else —
             an old record with no scope must still be saveable once another field is fixed. */
          <p
            id={errorId('geoAreaIds')}
            className={cn(
              'rounded-[10px] border border-dashed px-3.5 py-3 text-xs',
              errors.geoAreaIds
                ? 'border-rose-300 text-rose-600 dark:border-rose-800 dark:text-rose-400'
                : 'border-zinc-300 text-zinc-500 dark:border-zinc-700 dark:text-zinc-400'
            )}
          >
            {errors.geoAreaIds
              ? validationMessage(t, errors.geoAreaIds)
              : readOnly
                ? t('No area is attached to this scope yet.')
                : t('Add at least one area: the technician scope follows from it.')}
          </p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {selectedZones.map((zone) => (
              <ZoneChip
                key={zone.id}
                zone={zone}
                readOnly={readOnly}
                onRemove={() => onChange({ geoAreaIds: geoAreaIds.filter((areaId) => areaId !== zone.id) })}
              />
            ))}
          </ul>
        )}

        {/* F18 — an opt-in fallback, scored like the distance cap's edge; same card, same country. */}
        <div className="space-y-2 border-t border-zinc-200 pt-4 dark:border-zinc-800">
          <div className="flex items-center gap-1.5">
            <Route className="h-4 w-4 text-amber-600 dark:text-amber-400" aria-hidden="true" />
            <Label>{t('Secondary zone · possible fees')}</Label>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            {t('Departments where they agree to travel for a fee.')}
          </p>

          {!readOnly && (
            <MultiSelect
              id="technician-secondaryGeoAreaIds"
              aria-label={t('Secondary zone · possible fees')}
              options={secondaryOptions}
              selected={secondaryGeoAreaIds.map(String)}
              onChange={(values) => onChange({ secondaryGeoAreaIds: values.map(Number) })}
              disabled={!countryCode}
              searchable
              joinedLabels
              placeholder={
                countryCode && secondaryOptions.length === 0 ? t('No area for this country yet') : t('Select...')
              }
            />
          )}

          {secondarySelectedZones.length === 0 ? (
            <p className="rounded-[10px] border border-dashed border-zinc-300 px-3.5 py-3 text-xs text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
              {t('No secondary zone.')}
            </p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {secondarySelectedZones.map((zone) => (
                <ZoneChip
                  key={zone.id}
                  zone={zone}
                  secondary
                  readOnly={readOnly}
                  onRemove={() =>
                    onChange({ secondaryGeoAreaIds: secondaryGeoAreaIds.filter((areaId) => areaId !== zone.id) })
                  }
                />
              ))}
            </ul>
          )}
        </div>

        {/* The question the card raises and the design answers: the scope is areas, not shops. */}
        <p className="text-xs leading-[1.4] text-zinc-500 dark:text-zinc-400">
          {t('Points of sale are not attached to the technician: they are identified from the areas of this scope, and picked on the intervention itself.')}
        </p>
      </CardContent>

      {pendingCountry !== null && (
        <ConfirmDeleteDialog
          open
          tone="warning"
          title={t('Change the country?')}
          description={t('The areas already selected will be cleared: they belong to the current country.')}
          confirmLabel={t('Change the country')}
          onClose={() => setPendingCountry(null)}
          onConfirm={confirmCountry}
        />
      )}
    </Card>
  );
};

export default ScopeSection;
